import { setOpacity } from "@memorang/helpers/src/setOpacity";
import { xpParcer } from "@memorang/helpers/src/xpParcer";
import { LinearGradient } from "expo-linear-gradient";
import { Dimensions, Platform } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Button from "../Button";
import EmptyState from "../EmptyView";
import { FilterChips } from "../FilterChips";
import { LeaderList } from "../LeaderList/";
import LeaderboardListItem from "../LeaderboardListItem";
import LeaderboardPodium from "../LeaderboardPodium/";
import ScrollContainer from "../ScrollContainer";
import DataErrorSvg from "./DataError.svg";
import EmptyErrorSvg from "./EmptyError.svg";

type LeaderboardItemType = {
	username: string;
	xp: number;
	avatar: string;
	rank?: number;
};

export type FilterChipItem = {
	label: string;
	daysToReset: number;
};
export type PeriodType = "all_time" | "week" | "month";

export type LeaderBoardAppletProps = {
	leaderboards: LeaderboardItemType[];
	userLeaderboardItem?: LeaderboardItemType;
	topRankers: LeaderboardItemType[];
	selectedChip: PeriodType;
	avatarType?: "initials" | "identicon";
	isVisibleLeaderBoardDataError?: boolean;
	errorMessage?: string;
	errorDopInfo?: string;
	errorButtonText?: string;
	noLeaderboardDataDopInfo?: string;
	noLeaderboardDataMessage?: string;
	noLeaderboardDataButtonText?: string;
	handleSelectChip: (chip: string) => void;
	onErrorClick?: () => void;
	handleEmptyStateCtaClick?: () => void;
};

const LeaderBoardApplet = ({
	leaderboards,
	topRankers,
	avatarType,
	userLeaderboardItem,
	selectedChip,
	handleSelectChip,
	isVisibleLeaderBoardDataError: leaderBoardDataError = false,
	errorMessage = "Unable to load leaderboard data",
	errorDopInfo = "We are having issues processing leaderboard information. Make sure you have a stable internet connection and try again.",
	noLeaderboardDataDopInfo = "We are having issues processing leaderboard information. Make sure you have a stable internet connection and try again.",
	noLeaderboardDataMessage = "Unable to load leaderboard data",
	noLeaderboardDataButtonText = "Try again ",
	errorButtonText = "Try again ",
	onErrorClick,
	handleEmptyStateCtaClick,
}: LeaderBoardAppletProps) => {
	const theme = useAppTheme();
	const isWeb = Platform.OS === "web";
	const renderBackground = (children: React.ReactNode) => (
		<LinearGradient
			colors={[
				setOpacity(theme.colors.inversePrimary, 0.4),
				theme.colors.white,
			]}
			locations={[0, 0.2, 0.5]}
			style={{
				flex: 1,
				borderRadius: isWeb ? 16 : 0,
				...(isWeb && {
					borderWidth: 1,
					borderColor: theme.colors.outline,
					borderStyle: "solid",
				}),
			}}
		>
			{children}
		</LinearGradient>
	);

	const renderContent = () => (
		<>
			<Box
				width="100%"
				alignItems="center"
				paddingHorizontal={16}
				paddingVertical={10}
			>
				{selectedChip !== "all_time" || leaderboards.length > 3 ? (
					<FilterChips
						selectedChip={selectedChip}
						handleSelectChip={handleSelectChip}
					/>
				) : null}
			</Box>
			{leaderBoardDataError ? (
				<Box
					paddingHorizontal={16}
					paddingBottom={16}
					flex={1}
					gap={16}
					justifyContent="center"
					alignItems="center"
				>
					<Box>
						<DataErrorSvg width={168} height={168} />
					</Box>
					<Text variant="titleMedium">{errorMessage}</Text>
					<Text variant="bodySmall">{errorDopInfo}</Text>
					<Button mode="contained" onPress={onErrorClick}>
						{errorButtonText}
					</Button>
				</Box>
			) : (
				<>
					<ScrollContainer
						style={{
							flex: 1,
							gap: 16,
							paddingHorizontal: 16,
						}}
					>
						<Box alignItems="center">
							<Box
								flexDirection="row"
								width="100%"
								maxWidth={600}
								justifyContent="center"
							>
								{topRankers.map((item, index) => (
									<LeaderboardPodium
										key={`podium-${item.username}-${index}`}
										rank={index === 0 ? 2 : index === 1 ? 1 : 3}
										xp={xpParcer(item.xp)}
										title={item.username}
										image={item.avatar}
										isCurrentUser={
											item.username === userLeaderboardItem?.username
										}
									/>
								))}
							</Box>
						</Box>

						<Box overflow="scroll" flex={1} paddingBottom={16}>
							{leaderboards.length > 0 ? (
								<LeaderList
									items={leaderboards.slice(3).map((item, index) => ({
										rank: item.rank ?? index + 4,
										xp: xpParcer(item.xp),
										title: item.username,
										image: item.avatar,
										isCurrentUser:
											item.username === userLeaderboardItem?.username,
										showGradient:
											item.username === userLeaderboardItem?.username,
										avatarType,
									}))}
								/>
							) : (
								<Box padding={16} alignItems="center">
									<Box>No additional leaderboard entries found</Box>
								</Box>
							)}
						</Box>
					</ScrollContainer>
					{userLeaderboardItem && (
						<Box
							style={{
								boxShadow: `0px 0px 20px 3px ${setOpacity(theme.colors.shadow, 0.1)}`,
							}}
							borderRadius={16}
							padding={10}
							backgroundColor={theme.colors.background}
						>
							<LeaderboardListItem
								rank={userLeaderboardItem.rank}
								xp={xpParcer(userLeaderboardItem.xp)}
								title={"You"}
								image={userLeaderboardItem.avatar}
								isCurrentUser={true}
								avatarType={
									avatarType === "initials" ||
									!userLeaderboardItem.avatar?.endsWith(".svg")
										? "initials"
										: "identicon"
								}
								isStatic={true}
							/>
						</Box>
					)}
				</>
			)}
		</>
	);

	return (
		<Box
			width={"100%"}
			height={"100%"}
			overflow="hidden"
			maxHeight={isWeb ? Dimensions.get("window").height - 200 : undefined}
		>
			{leaderboards.length <= 3 ? (
				<EmptyState
					title={noLeaderboardDataMessage}
					subtitle={noLeaderboardDataDopInfo}
					cta={noLeaderboardDataButtonText}
					image={EmptyErrorSvg as never}
					handleAction={handleEmptyStateCtaClick}
				/>
			) : (
				<Box
					display="flex"
					borderRadius={isWeb ? 16 : 0}
					flex={1}
					style={{
						overflow: "hidden",
					}}
				>
					{renderBackground(renderContent())}
				</Box>
			)}
		</Box>
	);
};

export default LeaderBoardApplet;
